// src/pages/Connectors.jsx - FULLY INTEGRATED
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical, RefreshCw, Pause, Play, Settings, Trash2, ExternalLink, Plus, Sliders, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.jsx';
import { EmptyState } from '@/components/ui/empty-state.jsx';
import { apiCall } from '../utils/api';
import { canCreateConnector, canEditConnector, canDeleteConnector, canSyncConnector } from '../utils/roles';
import { API_BASE_URL } from '../config';

export default function Connectors() {
  const navigate = useNavigate();
  
  // State
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [syncingIds, setSyncingIds] = useState(new Set());
  const [error, setError] = useState(null);

  // Load connectors on mount
  useEffect(() => {
    loadConnectors();
  }, []);

  async function loadConnectors() {
    setLoading(true);
    setError(null);
    
    try {
      // 1. Get connector configs
      const configsRes = await apiCall('/sync/configs');
      if (!configsRes.ok) {
        throw new Error('Failed to load connectors');
      }
      const configsData = await configsRes.json();
      const configs = configsData.configs || [];

      // 2. Get connector health (admin only - handle 403 gracefully)
      let healthMap = {};
      try {
        const healthRes = await apiCall('/admin/connector-health');
        if (healthRes.ok) {
          const healthData = await healthRes.json();
          healthMap = Object.fromEntries(
            (healthData.connectors || []).map(c => [c.connector_config_id, c])
          );
        }
      } catch (err) {
        console.warn('Could not load connector health (requires admin role)');
      }

      // 3. Get stats for each connector (entity counts)
      const statsPromises = configs.map(cfg =>
        apiCall(`/sync/configs/${cfg.id}/stats`)
          .then(res => res.ok ? res.json() : null)
          .catch(() => null)
      );
      const statsResults = await Promise.all(statsPromises);
      const statsMap = Object.fromEntries(
        statsResults.filter(Boolean).map(s => [s.config_id, s])
      );

      // 4. Transform to UI format
      const transformed = configs.map(cfg => {
        const health = healthMap[cfg.id];
        const stats = statsMap[cfg.id];
        const configJson = cfg.config_json || {};
        
        const derived = deriveStatus(cfg, health);
        return {
          id: cfg.id,
          name: cfg.display_name || `Connector #${cfg.id}`,
          type: cfg.connector_key || 'unknown',
          status: derived.status,
          statusReason: derived.reason,
          lastSync: formatTimestamp(cfg.last_synced_at),
          entities: stats ? formatNumber(stats.total_records) : 'N/A',
          enabled: configJson.enabled !== false,
          rawConfig: cfg,
          rawHealth: health,
          rawStats: stats,
        };
      });

      setConnectors(transformed);
    } catch (err) {
      console.error('Failed to load connectors:', err);
      setError(err.message || 'Failed to load connectors');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefreshAll() {
    setIsRefreshing(true);
    
    try {
      const res = await apiCall('/sync/run-all', {
        method: 'POST'
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('Refresh all triggered:', data);
        
        // Wait a bit then reload
        setTimeout(() => {
          loadConnectors();
          setIsRefreshing(false);
        }, 2000);
      } else {
        throw new Error('Failed to refresh connectors');
      }
    } catch (err) {
      console.error('Refresh all failed:', err);
      alert('Failed to refresh connectors: ' + err.message);
      setIsRefreshing(false);
    }
  }
  async function handleReconnect(connectorId, connectorType) {
    try {
      const ticketRes = await apiCall('/oauth/ticket', { method: 'POST' });
      if (!ticketRes.ok) throw new Error('Failed to prepare reconnect');
      const { ticket } = await ticketRes.json();

      const startUrl = `${API_BASE_URL}/oauth/${connectorType}/start?config_id=${connectorId}&ticket=${encodeURIComponent(ticket)}`;
      const w = 600, h = 700;
      const left = window.screenX + (window.outerWidth - w) / 2;
      const top = window.screenY + (window.outerHeight - h) / 2;
      const popup = window.open(
        startUrl,
        `oauth_reconnect_${connectorId}`,
        `width=${w},height=${h},left=${left},top=${top},scrollbars=yes,resizable=yes`
      );
      if (!popup) {
        alert('Popup was blocked. Please allow popups for this site and try again.');
        return;
      }
      const iv = setInterval(() => {
        if (popup.closed) {
          clearInterval(iv);
          setTimeout(() => loadConnectors(), 1500);
        }
      }, 1000);
    } catch (e) {
      alert(e.message || 'Reconnect failed');
    }
  }



  async function handleSyncNow(connectorId) {
    setSyncingIds(prev => new Set([...prev, connectorId]));
    setConnectors(prev => prev.map(c =>
      c.id === connectorId ? { ...c, status: 'syncing' } : c
    ));

    try {
      const res = await apiCall(`/sync/configs/${connectorId}/run-now`, {
        method: 'POST',
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const detail = data?.detail || `Sync failed (HTTP ${res.status})`;
        if (res.status === 400) {
          const shouldDelete = window.confirm(
            `This connector (#${connectorId}) has no entities configured. ` +
            `It was likely created during a failed setup. Delete it and create a new connector instead?`
          );
          if (shouldDelete) {
            await handleDelete(connectorId);
          }
        } else {
          alert('Sync failed: ' + detail);
        }
        return;
      }

      // 202 success: server is running each entity sync in a background
      // task. Wait briefly, then refresh the list to surface new state.
      await new Promise(resolve => setTimeout(resolve, 3000));
      await loadConnectors();
    } catch (err) {
      console.error('Sync failed:', err);
      alert('Sync failed: ' + (err.message || 'unknown error'));
    } finally {
      setSyncingIds(prev => {
        const next = new Set(prev);
        next.delete(connectorId);
        return next;
      });
    }
  }

  async function handleTogglePause(connectorId) {
    const connector = connectors.find(c => c.id === connectorId);
    if (!connector) return;

    const newEnabledState = !connector.enabled;
    
    try {
      const res = await apiCall(`/sync/configs/${connectorId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          enabled: newEnabledState
        })
      });

      if (res.ok) {
        // Update local state immediately
        setConnectors(prev => prev.map(c =>
          c.id === connectorId
            ? { ...c, enabled: newEnabledState, status: newEnabledState ? 'active' : 'paused' }
            : c
        ));
      } else {
        throw new Error('Failed to update connector');
      }
    } catch (err) {
      console.error('Toggle pause failed:', err);
      alert('Failed to update connector: ' + err.message);
    }
  }

  async function handleDelete(connectorId) {
    const connector = connectors.find(c => c.id === connectorId);
    if (!connector) return;

    if (!window.confirm(`Delete "${connector.name}"? This will remove all synced data and cannot be undone.`)) {
      return;
    }

    try {
      const res = await apiCall(`/sync/configs/${connectorId}`, {
        method: 'DELETE'
      });

      if (res.status === 204 || res.ok) {
        // Remove from UI
        setConnectors(prev => prev.filter(c => c.id !== connectorId));
      } else if (res.status === 403) {
        alert('Admin role required to delete connectors');
      } else {
        throw new Error('Failed to delete connector');
      }
    } catch (err) {
      console.error('Delete failed:', err);
      if (err.message.includes('403') || err.message.includes('Forbidden')) {
        alert('Admin role required to delete connectors');
      } else {
        alert('Failed to delete connector: ' + err.message);
      }
    }
  }

  function handleConfigure(connectorId) {
    navigate(`/connectors/${connectorId}/configure`);
  }
  function handleFieldMapping(connectorId) {
    navigate(`/connectors/${connectorId}/fields`);
  }


  function handleViewDetails(connectorId) {
    // TODO: Navigate to details page when it exists
    // navigate(`/connectors/${connectorId}`);
    alert('Details page coming soon');
  }

  function handleNewConnector() {
    navigate('/connectors/new');
  }

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Connectors
            </h1>
            <p className="text-sm text-[#a0a0a0] mt-1">
              Loading connectors...
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-white">
              Connectors
            </h1>
            <p className="text-sm text-red-400 mt-1">
              {error}
            </p>
          </div>
        </div>
        <div className="text-center py-12">
          <p className="text-[#a0a0a0] mb-4">Failed to load connectors</p>
          <Button onClick={loadConnectors}>
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            Connectors
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Manage your data connectors and sync operations
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canSyncConnector() && (
            <Button
              variant="secondary"
              onClick={handleRefreshAll}
              disabled={isRefreshing}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh All
            </Button>
          )}
          {canCreateConnector() && (
            <Button onClick={handleNewConnector}>
              <Plus className="w-4 h-4" />
              New Connector
            </Button>
          )}
        </div>
      </div>

      {/* Connectors Table */}
      {connectors.length > 0 ? (
        <TableWrapper>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Connector</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Sync</TableHead>
                <TableHead>Entities</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {connectors.map((connector) => (
                <TableRow key={connector.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-semibold text-blue-500">
                          {connector.name.substring(0, 2).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-white">
                          {connector.name}
                        </p>
                        <p className="text-xs text-[#6a6a6a]">
                          {connector.type} • ID: {connector.id}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusBadge
                      status={connector.status}
                      reason={connector.statusReason}
                      isSyncing={syncingIds.has(connector.id)}
                      onClick={() => navigate(`/logs?connector=${connector.id}`)}
                    />
                  </TableCell>
                  <TableCell className="text-[#a0a0a0]">
                    {connector.lastSync}
                  </TableCell>
                  <TableCell className="text-[#a0a0a0]">
                    {connector.entities}
                  </TableCell>
                  <TableCell className="text-right">
                    <ConnectorActions
                      connector={connector}
                      isSyncing={syncingIds.has(connector.id)}
                      onSyncNow={handleSyncNow}
                      onTogglePause={handleTogglePause}
                      onConfigure={handleConfigure}
                      onReconnect={handleReconnect}
                      onDelete={handleDelete}
                      onViewDetails={handleViewDetails}
                      onFieldMapping={handleFieldMapping}
                    />

                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
          <EmptyState
            icon={Plus}
            title="No connectors yet"
            description="Get started by creating your first data connector to sync information from your tools."
            actionLabel={canCreateConnector() ? "New Connector" : undefined}
            onAction={canCreateConnector() ? handleNewConnector : undefined}
          />
        </div>
      )}
    </div>
  );
}

// ==========================================
// STATUS BADGE COMPONENT
// ==========================================
function StatusBadge({ status, reason, isSyncing, onClick }) {
  // Override with syncing if actively syncing
  const displayStatus = isSyncing ? 'syncing' : status;

  const getStatusConfig = (s) => {
    switch (s) {
      case 'active':
      case 'healthy':
        return { variant: 'success', label: 'Active' };
      case 'syncing':
        return { variant: 'info', label: 'Syncing' };
      case 'paused':
        return { variant: 'neutral', label: 'Paused' };
      case 'error':
        return { variant: 'error', label: 'Error' };
      case 'warning':
        return { variant: 'warning', label: 'Warning' };
      case 'unknown':
        return { variant: 'neutral', label: 'No data' };
      default:
        return { variant: 'neutral', label: 'Unknown' };
    }
  };

  const config = getStatusConfig(displayStatus);
  const tooltipText = isSyncing
    ? 'Sync in progress...'
    : (reason || 'No status info available.');

  // Click navigates to logs filtered by this connector
  const handleClick = (e) => {
    e.stopPropagation();
    if (onClick) onClick();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          onClick={handleClick}
          className="inline-block cursor-pointer hover:opacity-80 transition-opacity"
        >
          <Badge variant={config.variant}>{config.label}</Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-xs whitespace-normal text-left"
      >
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}

// ==========================================
// CONNECTOR ACTIONS DROPDOWN
// ==========================================
function ConnectorActions({ 
  connector, 
  isSyncing,
  onSyncNow, 
  onTogglePause, 
  onConfigure, 
  onDelete,
  onViewDetails,
  onFieldMapping,
  onReconnect,
}) {

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="inline-flex items-center justify-center w-8 h-8 text-[#a0a0a0] hover:text-white hover:bg-[#222222] rounded-lg transition-colors">
          <MoreVertical className="w-4 h-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem
          onClick={() => onViewDetails(connector.id)}
          className="cursor-pointer"
        >
          <ExternalLink className="mr-2 h-4 w-4" />
          <span>View Details</span>
        </DropdownMenuItem>
        
        {canSyncConnector() && (
          <DropdownMenuItem
            onClick={() => onSyncNow(connector.id)}
            className="cursor-pointer"
            disabled={isSyncing}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} />
            <span>{isSyncing ? 'Syncing...' : 'Sync Now'}</span>
          </DropdownMenuItem>
        )}
        {canEditConnector() && ['jira', 'github', 'slack', 'teams'].includes(connector.type) && (
          <DropdownMenuItem
            onClick={() => onReconnect(connector.id, connector.type)}
            className="cursor-pointer"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            <span>Reconnect</span>
          </DropdownMenuItem>
        )}

        {canEditConnector() && (
          <DropdownMenuItem
            onClick={() => onTogglePause(connector.id)}
            className="cursor-pointer"
          >
            {connector.enabled ? (
              <>
                <Pause className="mr-2 h-4 w-4" />
                <span>Pause Connector</span>
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                <span>Resume Connector</span>
              </>
            )}
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        
        {canEditConnector() && (
          <DropdownMenuItem
            onClick={() => onFieldMapping(connector.id)}
            className="cursor-pointer"
          >
            <Sliders className="mr-2 h-4 w-4" />
            <span>Field Mapping</span>
          </DropdownMenuItem>
        )}



        {canEditConnector() && (
          <DropdownMenuItem
            onClick={() => onConfigure(connector.id)}
            className="cursor-pointer"
          >
            <Settings className="mr-2 h-4 w-4" />
            <span>Configure</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {canDeleteConnector() && (
          <DropdownMenuItem
            onClick={() => onDelete(connector.id)}
            className="cursor-pointer text-red-400 hover:text-red-300 focus:text-red-300"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            <span>Delete</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ==========================================
// HELPER FUNCTIONS
// ==========================================

function deriveStatus(config, health) {
  // Returns { status, reason } so callers can render tooltips.
  const configJson = config.config_json || {};
  if (configJson.enabled === false) {
    return { status: 'paused', reason: 'Connector is paused. Enable it to resume syncing.' };
  }

  // Health data from backend (admin only). Use its status + reason directly.
  if (health) {
    const healthStatus = (health.status || '').toLowerCase();
    const reason = health.reason || '';
    if (healthStatus === 'healthy') return { status: 'active', reason: reason || 'All good.' };
    if (healthStatus === 'error') return { status: 'error', reason: reason || 'Last sync failed.' };
    if (healthStatus === 'warning') return { status: 'warning', reason: reason || 'Needs attention.' };
    if (healthStatus === 'paused') return { status: 'paused', reason: reason || 'Connector is paused.' };
    if (healthStatus === 'unknown') return { status: 'unknown', reason: reason || 'No syncs yet.' };
  }

  return { status: 'active', reason: 'No health data available.' };
}

function formatTimestamp(timestamp) {
  if (!timestamp) return 'Never';
  
  try {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}

function formatNumber(num) {
  if (num == null || isNaN(num)) return 'N/A';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toString();
}